/**
 * React Query hooks for analysis management
 * Provides type-safe data fetching and mutations for analyses
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';

import { creativesKeys } from '@/lib/queryClient';
import { api } from '@/services/api';
import type { Analysis } from '@/types';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const analysesKeys = {
  all: ['analyses'] as const,
  lists: () => [...analysesKeys.all, 'list'] as const,
  list: (filters: { creativeId?: string }) =>
    [...analysesKeys.lists(), filters] as const,
  details: () => [...analysesKeys.all, 'detail'] as const,
  detail: (id: string) => [...analysesKeys.details(), id] as const,
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch a single analysis by ID
 */
export function useAnalysis(
  analysisId?: string
): UseQueryResult<Analysis, Error> {
  return useQuery({
    queryKey: analysesKeys.detail(analysisId || ''),
    queryFn: async () => {
      if (!analysisId) {
        throw new Error('Analysis ID is required');
      }
      const analysis = await api.get<Analysis>(`/analyses/${analysisId}`);
      return analysis;
    },
    enabled: !!analysisId,
    staleTime: 5 * 60 * 1000, // 5 minutes (analysis results don't change)
  });
}

/**
 * Fetch all analyses for a creative
 */
export function useAnalyses(
  creativeId?: string
): UseQueryResult<Analysis[], Error> {
  return useQuery({
    queryKey: analysesKeys.list({ creativeId }),
    queryFn: async () => {
      if (!creativeId) {
        return [];
      }
      const analyses = await api.get<Analysis[]>(
        `/creatives/${creativeId}/analyses`
      );
      return analyses;
    },
    enabled: !!creativeId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch analysis with polling for pending/processing status
 * Automatically polls every 5 seconds when status is pending or processing
 */
export function useAnalysisStatus(
  analysisId?: string,
  creativeId?: string,
  options?: {
    enabled?: boolean;
    onComplete?: (analysis: Analysis) => void;
    onError?: (analysis: Analysis) => void;
  }
): UseQueryResult<Analysis, Error> {
  const { enabled = true, onComplete, onError } = options || {};

  return useQuery({
    queryKey: analysesKeys.detail(analysisId || ''),
    queryFn: async () => {
      if (!analysisId) {
        throw new Error('Analysis ID is required');
      }
      // Use nested endpoint when creativeId available (query vs scan)
      const endpoint = creativeId
        ? `/creatives/${creativeId}/analyses/${analysisId}`
        : `/analyses/${analysisId}`;
      const analysis = await api.get<Analysis>(endpoint);

      // Call callbacks based on status
      if (analysis.status === 'complete' && onComplete) {
        onComplete(analysis);
      }
      if (analysis.status === 'failed' && onError) {
        onError(analysis);
      }

      return analysis;
    },
    enabled: !!analysisId && enabled,
    staleTime: 0, // Always refetch when polling
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll every 5 seconds if not complete/failed
      if (data?.status && data.status !== 'complete' && data.status !== 'failed') {
        return 5000;
      }
      // Stop polling when complete or failed
      return false;
    },
  });
}

/**
 * Get the latest analysis for a creative
 */
export function useLatestAnalysis(creativeId?: string) {
  const analysesQuery = useAnalyses(creativeId);

  return {
    ...analysesQuery,
    data: analysesQuery.data?.[0], // Assuming analyses are sorted by date desc
  };
}

/**
 * Alias for useAnalyses for consistency with task naming
 * Returns all analyses for a creative, sorted by date descending
 */
export function useAnalysisList(creativeId?: string): UseQueryResult<Analysis[], Error> {
  return useAnalyses(creativeId);
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Trigger a new analysis for a creative
 */
export function useTriggerAnalysis(): UseMutationResult<
  Analysis,
  Error,
  { creativeId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ creativeId }) => {
      const analysis = await api.post<Analysis>(`/creatives/${creativeId}/analyze`);
      return analysis;
    },
    onSuccess: (newAnalysis, variables) => {
      // Invalidate analyses list for this creative
      queryClient.invalidateQueries({
        queryKey: analysesKeys.list({ creativeId: variables.creativeId }),
      });

      // Add new analysis to cache
      queryClient.setQueryData<Analysis[]>(
        analysesKeys.list({ creativeId: variables.creativeId }),
        (old) => {
          if (!old) return [newAnalysis];
          return [newAnalysis, ...old];
        }
      );

      // Set the new analysis detail in cache
      queryClient.setQueryData(
        analysesKeys.detail(newAnalysis.analysisId),
        newAnalysis
      );

      // Invalidate creative to update analysis count
      queryClient.invalidateQueries({
        queryKey: creativesKeys.detail(variables.creativeId),
      });
    },
    onError: (error) => {
      console.error('Failed to trigger analysis:', error);
    },
  });
}
